import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { useHistory } from 'react-router-dom';
import PrescriptionInterface from '../../interfaces/prescription';

import { AppState } from '../../store';
import { fetchPrescription as fetch, dispensePrescription as dispense } from '../../store/prescription/actions';

import { ItemInterface as HeaderItemInterface } from '../../components/ui/List/LabelValue';
import Prescription from './Prescription';

import Sentry from '../../monitoring/sentry';
import { PrescriptionError } from '../../store/prescription/types';
import { trackPageView } from '../../monitoring/page-view';
import { useTrackingProperties } from '../../monitoring/utils';
import { interactionEventPages } from '../../monitoring/interaction/dispensation-home';

interface StateProps {
  loading: boolean;
  error: PrescriptionError;
  prescription: PrescriptionInterface;
  dispensation: {
    errors: string[];
    loading: boolean;
  };
}

interface DispatchProps {
  fetchPrescription: Function;
  dispensePrescription: Function;
  fetchPermissions: Function;
}

interface RouterProps {
  match: {
    params: {
      token: string;
    };
  };
  location: {
    search: string;
  };
}

type Props = StateProps & DispatchProps & RouterProps;

const PrescriptionContainer: React.FC<Props> = ({
  loading,
  error,
  fetchPrescription,
  dispensePrescription,
  match,
  location,
  prescription,
  dispensation,
}: Props) => {
  const { drugstoreId, drugstoreChainId, drugstoreName, drugstoreUF, chainName } = useTrackingProperties()

  useEffect(() => {
    if (!drugstoreId || !drugstoreChainId || drugstoreId === 'undefined' || drugstoreChainId === 'undefined') {
      return;
    }
    trackPageView({
      page: interactionEventPages.dispensador_detalhes_receita,
      properties: {
        drugstore_id: drugstoreId,
        drugstore_chain_id: drugstoreChainId,
        drugstore_name: drugstoreName,
        drugstore_chain_name: chainName,
        drugstore_uf: drugstoreUF,
      },
    });
  }, [drugstoreId, drugstoreChainId, drugstoreName, drugstoreUF, chainName])
  const [headerItems, setHeaderItems] = useState<Array<HeaderItemInterface>>([]);

  const history = useHistory();

  // @TODO: Por algum motivo o date vem null do backend
  const formatDateToBrazilPattern = (date: Date): string => {
    if (!date) return '';
    return new Intl.DateTimeFormat('pt-BR').format(new Date(date.toString().replace(/-/gm, '/')));
  };

  const handleDispense = (): void => {
    dispensePrescription(prescription);
  };

  useEffect(() => {
    if (!prescription) {
      return;
    }

    const { doctor } = prescription;

    setHeaderItems([
      {
        label: 'Profissional:',
        value:
          `${doctor.abbreviation} ${doctor.name}, 
          ${doctor.regional_code} ${doctor.board_number} 
          ${doctor.board_state}`,
      },
      ...(prescription.patient.social_name
        ? [
          {
            label: 'Paciente / Nome Civil:',
            value: prescription.patient.name,
            disabled: true,
          },
          {
            label: 'Paciente / Nome Social:',
            value: prescription.patient.social_name,
          },
        ]
        : [
          {
            label: 'Paciente / Nome Civil:',
            value: prescription.patient.name,
          }
        ]
      ),
      {
        label: 'Data da prescrição:',
        value: prescription.prescription_date
          ? formatDateToBrazilPattern(prescription.prescription_date)
          : 'Sem data',
      },
    ]);

    Sentry.setPrescriptionTags(prescription);
  }, [prescription]);


  useEffect(() => {
    if (!prescription.id) {
      fetchPrescription(match.params.token)
    }
  }, [prescription.id, fetchPrescription, match.params.token, location]);

  useEffect(() => {
    if (error.exists) {
      history.push('/');
    }
  }, [error, history])

  return (
    <Prescription
      error={error.exists}
      loading={loading}
      prescription={prescription}
      headerItems={headerItems}
      dispensePrescription={handleDispense}
      dispensation={dispensation}
    />
  );
};

const mapStateToProps = ({ prescription }: AppState): StateProps => ({
  loading: prescription.loading,
  error: prescription.error,
  prescription: prescription.prescription,
  dispensation: prescription.dispensation,
});

const mapDispatchToProps = {
  fetchPrescription: fetch,
  dispensePrescription: dispense,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrescriptionContainer);
